import {
    useImperativeHandle,
    forwardRef,
    useEffect,
    useState,
    useRef,
    useId
} from "react";
import {
    type GestureResponderEvent,
    type NativeSyntheticEvent,
    type NativeScrollEvent,
    type LayoutChangeEvent,
    useWindowDimensions,
    type PointerEvent,
    PanResponder,
    ScrollView,
    Animated,
    Platform,
    View
} from "react-native";
import type IBottomSheetProps from "./type";
import type {
    BottomSheetRestingHeight,
    BottomSheetGestureType,
    IBottomSheetRef
} from "./type";
import stylesheet, {
    BOTTOM_SHEET_PORTAL_NAME,
    getBottomSheetCloseStyle,
    useStyles
} from "./stylesheet";
import {
    getBottomSheetNaturalHeight,
    getBottomSheetMaxSnapHeight,
    getBottomSheetIsDragStarted,
    getBottomSheetPivotHeight,
    getBottomSheetIsScrollEnd,
    getBottomSheetDragFrame,
    getBottomSheetMomentum,
    getBottomSheetVelocity,
    getBottomSheetRelease,
    getBottomSheetHeights,
    BOTTOM_SHEET_GESTURE
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    type RefForwardingComponent,
    type SafePointerEvent,
    type NCoreUIKitIcon,
    safeGlobal
} from "../../types";
import {
    useReducedMotion,
    MOTION_SPRINGS,
    MOTION_EASINGS
} from "../../helpers/motion";
import {
    usePointerType
} from "../../helpers/pointer";
import {
    useSafeAreaInsets
} from "react-native-safe-area-context";
import type {
    IModalRef
} from "../modal/type";
import {
    X as XIcon
} from "lucide-react-native";
import {
    getModalMotion
} from "../modal/stylesheet";
import {
    warnOnce
} from "../../utils/development";
import {
    uuid
} from "../../utils";
import AnimatedIcon from "../animatedIcon";
import Loading from "../loading";
import Button from "../button";
import Modal from "../modal";
import Text from "../text";

const BottomSheetCloseIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => <AnimatedIcon
    customColor={customColor}
    animationTrigger="hover"
    animation="signature"
    color={color}
    icon={XIcon}
    size={size}
/>;

const BottomSheet: RefForwardingComponent<IBottomSheetRef, IBottomSheetProps> = ({
    isContentReady: isContentReadyProp = true,
    isForceFullScreenOnSwipe = false,
    handleContainerBackgroundColor,
    isCanFullScreenOnSwipe = false,
    renderBottom: renderBottomProp,
    renderHeader: renderHeaderProp,
    isActive: isActiveDeprecated,
    isWrapSafeAreaContext = true,
    backgroundColor = "raised",
    isWorkAsFullScreen = false,
    isWorkWithAnimation = true,
    scrollEndThreshold = 0.85,
    isCloseOnOverlay = true,
    isCustomPadding = false,
    isWorkWithPortal = true,
    handleContainerSpacing,
    handleBackgroundColor,
    isAutoHeight = false,
    isShowHandle = true,
    isSwipeClose = true,
    isCanSwipe = true,
    onOverlayPressed,
    renderSubHeader,
    scrollViewProps,
    scrollViewStyle,
    customLocalize,
    handleHeight,
    onOpenChange,
    customTheme,
    description,
    isShowClose,
    onScrollEnd,
    modalProps,
    portalName,
    customKey,
    id: outID,
    snapPoint,
    children,
    onClosed,
    onOpened,
    onClose,
    isOpen,
    onOpen,
    style,
    title,
    ...props
}, ref) => {
    const {
        controlHeights,
        shapeConfigs,
        touchTargets,
        shadowAlpha,
        elevations,
        typography,
        durations,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const isReduceMotionEnabled = useReducedMotion();

    const insets = useSafeAreaInsets();

    const isActiveProp = isOpen ?? isActiveDeprecated;

    const {
        height: windowHeight
    } = useWindowDimensions();

    const baseId = useId();

    const [
        isActive,
        setIsActive
    ] = useState(isActiveProp ?? false);

    const [
        isContentReady,
        setIsContentReady
    ] = useState(isContentReadyProp);

    const [
        containerHeight,
        setContainerHeight
    ] = useState(windowHeight);

    const [
        isHeightMeasured,
        setIsHeightMeasured
    ] = useState(false);

    const [
        isScrolled,
        setIsScrolled
    ] = useState(false);

    const topInset = isWrapSafeAreaContext && isWorkWithPortal && !isForceFullScreenOnSwipe ? insets.top : 0;

    const bottomSafeArea = isWrapSafeAreaContext && isWorkWithPortal ? insets.bottom : 0;

    const currentHeights = getBottomSheetHeights({
        isCanFullScreenOnSwipe,
        isWorkAsFullScreen,
        containerHeight,
        isAutoHeight,
        snapPoint,
        topInset,
        spaces
    });

    const id = useRef(outID ? outID : uuid());

    const bottomSheetKey = useRef(customKey ? customKey : uuid());

    const modalRef = useRef<IModalRef>(null);

    const scrollViewRef = useRef<ScrollView>(null);

    const translateYAnim = useRef(new Animated.Value(Math.max(containerHeight, windowHeight))).current;

    const heightAnim = useRef(new Animated.Value(currentHeights.fixedPivotHeight ?? 0)).current;

    const translateYValue = useRef(Math.max(containerHeight, windowHeight));

    const heightValue = useRef(currentHeights.fixedPivotHeight ?? 0);

    const sheetHeight = useRef(-1);

    const bodyHeight = useRef(-1);

    const contentHeight = useRef(-1);

    const scrollOffset = useRef(0);

    const gesture = useRef<BottomSheetGestureType | null>(null);

    const restingHeight = useRef<BottomSheetRestingHeight>("pivotHeight");

    const momentumAnimation = useRef<Animated.CompositeAnimation | null>(null);

    const removeDragListeners = useRef<(() => void) | undefined>(undefined);

    const openTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    const closeCallbacks = useRef<Array<(props: {
        id: string;
    }) => void>>([]);

    const isActiveRef = useRef(isActive);

    const isOpeningRef = useRef(isActive);

    const isClosingRef = useRef(false);

    const isReopenRequestedRef = useRef(false);

    const isReopenPendingRef = useRef(false);

    const isHeightAnimatingRef = useRef(false);

    const isDragReleasedRef = useRef(false);

    const isGestureInBody = useRef(false);

    const isScrollEndReached = useRef(false);

    const titleId = `${baseId}-title`;

    const descriptionId = `${baseId}-description`;

    const isTitleRendered = !renderHeaderProp && !!title;

    const isDescriptionRendered = !renderHeaderProp && !!description;

    const headerContent = renderHeaderProp ? renderHeaderProp() : null;

    const footerContent = renderBottomProp ? renderBottomProp() : null;

    const isCloseVisible = isShowClose ?? (isTitleRendered || isDescriptionRendered);

    const isHeaderVisible = !!headerContent || isTitleRendered || isDescriptionRendered || isCloseVisible;

    const isFooterVisible = !!footerContent;

    const isDragEnabled = pointerType === "coarse" && isCanSwipe;

    const isHandleVisible = pointerType === "coarse" && isShowHandle && !isWorkAsFullScreen;

    const isScrollEnabled = Platform.OS === "web" || !isDragEnabled;

    const isHeightAnimated = currentHeights.isHeightControlled && (currentHeights.fixedPivotHeight !== undefined || isHeightMeasured);

    const titleWebProps = Platform.OS === "web" ? {
        tabIndex: -1 as const
    } : {};

    const currentMotion = getModalMotion({
        closeAnimationDelay: modalProps?.closeAnimationDelay,
        openAnimationDelay: modalProps?.openAnimationDelay,
        isReduceMotionEnabled,
        durations
    });

    const closeStyle = getBottomSheetCloseStyle({
        isHandleVisible,
        controlHeights,
        touchTargets,
        pointerType,
        typography,
        iconSizes,
        radiuses,
        borders,
        spaces
    });

    const {
        headerTextContainer: headerTextContainerDynamicStyle,
        handleContainer: handleContainerDynamicStyle,
        bodyContent: bodyContentDynamicStyle,
        loadingBody: loadingBodyDynamicStyle,
        container: containerDynamicStyle,
        footer: footerDynamicStyle,
        handle: handleDynamicStyle,
        header: headerDynamicStyle,
        close: closeDynamicStyle
    } = useStyles({
        maxHeight: currentHeights.maxHeight,
        handleContainerBackgroundColor,
        handleContainerSpacing,
        handleBackgroundColor,
        isWorkAsFullScreen,
        backgroundColor,
        isCustomPadding,
        isFooterVisible,
        isHandleVisible,
        isHeaderVisible,
        bottomSafeArea,
        controlHeights,
        isCloseVisible,
        shapeConfigs,
        handleHeight,
        shadowAlpha,
        closeStyle,
        elevations,
        isScrolled,
        typography,
        radiuses,
        borders,
        colors,
        spaces
    });

    const getOffscreenOffset = () => {
        return Math.max(containerHeight, windowHeight);
    };

    const getHiddenOffset = () => {
        return Math.max(translateYValue.current, sheetHeight.current > 0 ? sheetHeight.current : getOffscreenOffset());
    };

    const getPivotHeight = () => {
        return getBottomSheetPivotHeight({
            isHeightControlled: currentHeights.isHeightControlled,
            fixedPivotHeight: currentHeights.fixedPivotHeight,
            naturalHeight: getBottomSheetNaturalHeight({
                contentHeight: contentHeight.current,
                sheetHeight: sheetHeight.current,
                bodyHeight: bodyHeight.current
            }),
            maxHeight: currentHeights.maxHeight,
            sheetHeight: sheetHeight.current
        });
    };

    const getMaxSnapHeight = (pivotHeight: number) => {
        return getBottomSheetMaxSnapHeight({
            availableHeight: currentHeights.availableHeight,
            isExpandable: currentHeights.isExpandable,
            pivotHeight
        });
    };

    const getMaxScroll = () => {
        return Math.max(0, contentHeight.current - bodyHeight.current);
    };

    const getCurrentHeight = () => {
        return isHeightAnimated ? heightValue.current : Math.max(0, sheetHeight.current);
    };

    const updateIsScrolled = (offset: number) => {
        setIsScrolled(offset > 0);
    };

    const scrollBodyTo = (offset: number) => {
        scrollOffset.current = offset;

        scrollViewRef.current?.scrollTo({
            animated: false,
            y: offset
        });

        updateIsScrolled(offset);
    };

    const checkScrollEnd = () => {
        if(!onScrollEnd) {
            return;
        }

        const isScrollEnd = getBottomSheetIsScrollEnd({
            contentHeight: contentHeight.current,
            scrollOffset: scrollOffset.current,
            bodyHeight: bodyHeight.current,
            scrollEndThreshold
        });

        if(!isScrollEnd) {
            isScrollEndReached.current = false;

            return;
        }

        if(isScrollEndReached.current) {
            return;
        }

        isScrollEndReached.current = true;

        onScrollEnd();
    };

    const clearOpenTimeout = () => {
        if(openTimeout.current === null) {
            return;
        }

        clearTimeout(openTimeout.current);

        openTimeout.current = null;
    };

    const stopMomentum = () => {
        if(!momentumAnimation.current) {
            return;
        }

        momentumAnimation.current.stop();

        momentumAnimation.current = null;
    };

    const stopSheetMotion = () => {
        gesture.current = null;

        isHeightAnimatingRef.current = false;

        clearOpenTimeout();

        removeDragListeners.current?.();

        stopMomentum();

        translateYAnim.stopAnimation();

        heightAnim.stopAnimation();
    };

    const flushCloseCallbacks = () => {
        closeCallbacks.current.splice(0).forEach((callback) => {
            callback({
                id: id.current
            });
        });
    };

    const syncRestingHeight = () => {
        if(!isHeightAnimated || gesture.current || isClosingRef.current || isHeightAnimatingRef.current) {
            return;
        }

        const pivotHeight = getPivotHeight();

        const targetHeight = restingHeight.current === "maxSnapHeight" ? getMaxSnapHeight(pivotHeight) : pivotHeight;

        if(Math.abs(heightValue.current - targetHeight) < BOTTOM_SHEET_GESTURE.edgeTolerance) {
            return;
        }

        heightAnim.setValue(targetHeight);
    };

    const focusTitle = () => {
        if(Platform.OS !== "web" || !isTitleRendered || modalProps?.initialFocusRef || typeof document === "undefined") {
            return;
        }

        const titleElement = document.getElementById(titleId);

        if(titleElement) {
            titleElement.focus({
                preventScroll: true
            });
        }
    };

    const startOpening = (isForced: boolean) => {
        if(!isOpeningRef.current || sheetHeight.current < 0) {
            return;
        }

        if(!isContentReady && !isForced) {
            if(openTimeout.current === null) {
                openTimeout.current = setTimeout(() => {
                    openTimeout.current = null;

                    sheetActions.current.startOpening(true);
                }, durations.short);
            }

            return;
        }

        clearOpenTimeout();

        isOpeningRef.current = false;

        translateYAnim.setValue(sheetHeight.current);

        if(onOpen) onOpen();

        focusTitle();

        Animated.timing(translateYAnim, {
            duration: currentMotion.openMoveDuration,
            easing: currentMotion.openEasing,
            useNativeDriver: false,
            toValue: 0
        }).start(({
            finished
        }) => {
            if(finished) {
                sheetActions.current.onOpenEnd();
            }
        });
    };

    const onOpenEnd = () => {
        if(onOpened) onOpened();
    };

    const finishClose = () => {
        stopSheetMotion();

        isClosingRef.current = false;

        isOpeningRef.current = false;

        isActiveRef.current = false;

        sheetHeight.current = -1;

        bodyHeight.current = -1;

        contentHeight.current = -1;

        scrollOffset.current = 0;

        restingHeight.current = "pivotHeight";

        isScrollEndReached.current = false;

        translateYAnim.setValue(getOffscreenOffset());

        setIsActive(false);

        setIsHeightMeasured(false);

        setIsScrolled(false);

        if(isReopenRequestedRef.current) {
            isReopenRequestedRef.current = false;

            isReopenPendingRef.current = true;

            flushCloseCallbacks();

            return;
        }

        if(onClosed) onClosed({
            id: id.current
        });

        flushCloseCallbacks();
    };

    const closeAnimation = (_onClosed?: (props: {
        id: string;
    }) => void) => {
        const isDragReleased = isDragReleasedRef.current;

        isDragReleasedRef.current = false;

        isReopenRequestedRef.current = false;

        isReopenPendingRef.current = false;

        if(_onClosed) {
            closeCallbacks.current.push(_onClosed);
        }

        if(!isActiveRef.current) {
            flushCloseCallbacks();

            return;
        }

        if(isClosingRef.current) {
            return;
        }

        const hiddenOffset = getHiddenOffset();

        stopSheetMotion();

        isClosingRef.current = true;

        isOpeningRef.current = false;

        if(onClose) onClose({
            id: id.current
        });

        if(!isReduceMotionEnabled && isDragReleased) {
            Animated.spring(translateYAnim, {
                ...MOTION_SPRINGS.standard,
                useNativeDriver: false,
                toValue: hiddenOffset
            }).start();
        } else if(!isReduceMotionEnabled) {
            Animated.timing(translateYAnim, {
                duration: currentMotion.closeMoveDuration,
                easing: currentMotion.closeEasing,
                useNativeDriver: false,
                toValue: hiddenOffset
            }).start();
        }

        if(!modalRef.current) {
            finishClose();

            return;
        }

        modalRef.current.closeAnimation(() => {
            sheetActions.current.finishClose();
        });
    };

    const requestClose = () => {
        if(isActiveRef.current && !isClosingRef.current && onOpenChange) onOpenChange(false);

        closeAnimation();
    };

    const closeImmediately = () => {
        isReopenRequestedRef.current = false;

        isReopenPendingRef.current = false;

        if(!isActiveRef.current || isClosingRef.current) {
            return;
        }

        modalRef.current?.setIsActive(false);

        finishClose();
    };

    const open = () => {
        if(isActiveRef.current) {
            if(isClosingRef.current) {
                isReopenRequestedRef.current = true;
            }

            return;
        }

        isActiveRef.current = true;

        isOpeningRef.current = true;

        translateYAnim.setValue(getOffscreenOffset());

        if(currentHeights.fixedPivotHeight !== undefined) {
            heightAnim.setValue(currentHeights.fixedPivotHeight);
        }

        setIsActive(true);
    };

    const snapTo = (targetHeight: number, maxSnapHeight: number) => {
        restingHeight.current = currentHeights.isExpandable && targetHeight >= maxSnapHeight - BOTTOM_SHEET_GESTURE.edgeTolerance ? "maxSnapHeight" : "pivotHeight";

        if(isReduceMotionEnabled) {
            translateYAnim.setValue(0);

            if(isHeightAnimated) heightAnim.setValue(targetHeight);

            return;
        }

        const animations = [
            Animated.spring(translateYAnim, {
                ...MOTION_SPRINGS.standard,
                useNativeDriver: false,
                toValue: 0
            })
        ];

        if(isHeightAnimated) {
            isHeightAnimatingRef.current = true;

            animations.push(Animated.spring(heightAnim, {
                ...MOTION_SPRINGS.standard,
                useNativeDriver: false,
                toValue: targetHeight
            }));
        }

        Animated.parallel(animations).start(() => {
            sheetActions.current.onSnapEnd();
        });
    };

    const onSnapEnd = () => {
        isHeightAnimatingRef.current = false;

        syncRestingHeight();
    };

    const startMomentum = (velocity: number) => {
        const momentum = getBottomSheetMomentum({
            scrollOffset: scrollOffset.current,
            maxScroll: getMaxScroll(),
            velocity
        });

        const scrollAnim = new Animated.Value(scrollOffset.current);

        const listenerId = scrollAnim.addListener(({
            value
        }) => {
            sheetActions.current.scrollBodyTo(value);
        });

        const animation = Animated.timing(scrollAnim, {
            toValue: momentum.scrollOffset,
            easing: MOTION_EASINGS.enter,
            duration: momentum.duration,
            useNativeDriver: false
        });

        momentumAnimation.current = animation;

        animation.start(({
            finished
        }) => {
            scrollAnim.removeListener(listenerId);

            if(momentumAnimation.current === animation) {
                momentumAnimation.current = null;
            }

            if(finished) {
                sheetActions.current.checkScrollEnd();
            }
        });
    };

    const getIsDragCaptured = (dx: number, dy: number) => {
        if(!isDragEnabled || !isActiveRef.current || isClosingRef.current) {
            return false;
        }

        return getBottomSheetIsDragStarted({
            slop: BOTTOM_SHEET_GESTURE.nativeSlop,
            dx,
            dy
        });
    };

    const onDragStart = (isFromChrome: boolean) => {
        if(!isActiveRef.current || isClosingRef.current) {
            return;
        }

        const startGesture = {
            startTranslateY: translateYValue.current,
            startScrollOffset: scrollOffset.current,
            startHeight: getCurrentHeight(),
            isFromChrome
        };

        gesture.current = startGesture;

        stopMomentum();

        translateYAnim.stopAnimation();

        heightAnim.stopAnimation();

        isHeightAnimatingRef.current = false;

        startGesture.startTranslateY = translateYValue.current;

        startGesture.startScrollOffset = scrollOffset.current;

        startGesture.startHeight = getCurrentHeight();
    };

    const onDragMove = (dy: number) => {
        if(!gesture.current) {
            return;
        }

        const pivotHeight = getPivotHeight();

        const frame = getBottomSheetDragFrame({
            startScrollOffset: gesture.current.startScrollOffset,
            startTranslateY: gesture.current.startTranslateY,
            maxSnapHeight: getMaxSnapHeight(pivotHeight),
            startHeight: gesture.current.startHeight,
            maxScroll: getMaxScroll(),
            isCanFullScreenOnSwipe,
            isWorkAsFullScreen,
            pivotHeight,
            dy
        });

        translateYAnim.setValue(frame.translateY);

        if(isHeightAnimated) heightAnim.setValue(frame.height);

        if(frame.scrollOffset !== scrollOffset.current) {
            scrollBodyTo(frame.scrollOffset);
        }
    };

    const onDragEnd = (velocity: number, isCancelled: boolean) => {
        const currentGesture = gesture.current;

        gesture.current = null;

        if(!currentGesture || !isActiveRef.current || isClosingRef.current) {
            return;
        }

        const pivotHeight = getPivotHeight();

        const maxSnapHeight = getMaxSnapHeight(pivotHeight);

        const release = getBottomSheetRelease({
            isFromChrome: currentGesture.isFromChrome,
            translateY: translateYValue.current,
            scrollOffset: scrollOffset.current,
            height: getCurrentHeight(),
            maxScroll: getMaxScroll(),
            isCanFullScreenOnSwipe,
            maxSnapHeight,
            isSwipeClose,
            pivotHeight,
            isCancelled,
            velocity
        });

        if(release.action === "close") {
            isDragReleasedRef.current = true;

            requestClose();

            return;
        }

        if(release.action === "momentum") {
            startMomentum(velocity);

            return;
        }

        if(release.action === "settle") {
            checkScrollEnd();

            return;
        }

        snapTo(release.height, maxSnapHeight);
    };

    const sheetActions = useRef({
        getIsDragCaptured,
        checkScrollEnd,
        scrollBodyTo,
        startOpening,
        finishClose,
        onDragStart,
        onDragMove,
        onDragEnd,
        onOpenEnd,
        onSnapEnd
    });

    const panResponder = useRef(PanResponder.create({
        onMoveShouldSetPanResponderCapture: (_, gestureState) => {
            return sheetActions.current.getIsDragCaptured(gestureState.dx, gestureState.dy);
        },
        onPanResponderGrant: () => {
            sheetActions.current.onDragStart(!isGestureInBody.current);
        },
        onPanResponderRelease: (_, gestureState) => {
            sheetActions.current.onDragEnd(gestureState.vy, false);
        },
        onPanResponderMove: (_, gestureState) => {
            sheetActions.current.onDragMove(gestureState.dy);
        },
        onPanResponderTerminationRequest: () => false,
        onPanResponderTerminate: () => {
            sheetActions.current.onDragEnd(0, true);
        },
        onStartShouldSetPanResponder: () => false,
        onShouldBlockNativeResponder: () => true
    })).current;

    useEffect(() => {
        sheetActions.current = {
            getIsDragCaptured,
            checkScrollEnd,
            scrollBodyTo,
            startOpening,
            finishClose,
            onDragStart,
            onDragMove,
            onDragEnd,
            onOpenEnd,
            onSnapEnd
        };
    });

    useEffect(() => {
        const translateYListener = translateYAnim.addListener(({
            value
        }) => {
            translateYValue.current = value;
        });

        const heightListener = heightAnim.addListener(({
            value
        }) => {
            heightValue.current = value;
        });

        return () => {
            translateYAnim.removeListener(translateYListener);

            heightAnim.removeListener(heightListener);

            if(openTimeout.current !== null) {
                clearTimeout(openTimeout.current);
            }

            removeDragListeners.current?.();

            momentumAnimation.current?.stop();

            translateYAnim.stopAnimation();

            heightAnim.stopAnimation();
        };
    }, []);

    useEffect(() => {
        if(isActive || !isReopenPendingRef.current) {
            return;
        }

        isReopenPendingRef.current = false;

        open();
    }, [
        isActive
    ]);

    useEffect(() => {
        if(isActiveDeprecated !== undefined) {
            warnOnce({
                message: "BottomSheet: \"isActive\" is deprecated and will be removed in the next major version. Use \"isOpen\" instead."
            });
        }

        if(onOpen) {
            warnOnce({
                message: "BottomSheet: \"onOpen\" is deprecated and will be removed in the next major version. Use \"onOpened\" instead."
            });
        }

        if(onClose) {
            warnOnce({
                message: "BottomSheet: \"onClose\" is deprecated and will be removed in the next major version. Use \"onOpenChange\" instead."
            });
        }
    }, [
        isActiveDeprecated,
        onClose,
        onOpen
    ]);

    useEffect(() => {
        if(isActiveProp === undefined) {
            return;
        }

        if(isActiveProp) {
            open();

            return;
        }

        if(isWorkWithAnimation) {
            closeAnimation();

            return;
        }

        closeImmediately();
    }, [
        isActiveProp
    ]);

    useEffect(() => {
        setIsContentReady(isContentReadyProp);
    }, [
        isContentReadyProp
    ]);

    useEffect(() => {
        if(isActiveRef.current) {
            return;
        }

        setContainerHeight(windowHeight);
    }, [
        windowHeight
    ]);

    useEffect(() => {
        syncRestingHeight();
    }, [
        currentHeights.fixedPivotHeight,
        currentHeights.availableHeight,
        currentHeights.maxHeight
    ]);

    useImperativeHandle(
        ref,
        () => ({
            setIsContentReady: (status) => {
                setIsContentReady(status);
            },
            close: (_onClosed?) => {
                closeAnimation(_onClosed);
            },
            open: () => {
                open();
            }
        })
    );

    const onContainerLayout = (event: LayoutChangeEvent) => {
        if(modalProps?.onContainerLayout) modalProps.onContainerLayout(event);

        const nextHeight = event.nativeEvent.layout.height;

        if(nextHeight > 0 && nextHeight !== containerHeight) {
            setContainerHeight(nextHeight);
        }
    };

    const onSheetLayout = (event: LayoutChangeEvent) => {
        sheetHeight.current = event.nativeEvent.layout.height;

        if(currentHeights.isHeightControlled && !isHeightAnimated && !isHeightMeasured) {
            heightAnim.setValue(Math.min(sheetHeight.current, currentHeights.maxHeight));

            setIsHeightMeasured(true);
        } else {
            syncRestingHeight();
        }

        startOpening(false);
    };

    const onSheetTouchEnd = () => {
        isGestureInBody.current = false;
    };

    const onSheetPointerDown = (event: PointerEvent) => {
        const isFromChrome = !isGestureInBody.current;

        isGestureInBody.current = false;

        if(!isActiveRef.current || isClosingRef.current) {
            return;
        }

        removeDragListeners.current?.();

        const startY = event.nativeEvent.clientY;

        let previousTime = Date.now();

        let isDragging = false;

        let previousY = startY;

        let velocity = 0;

        const onPointerMove = (moveEvent: SafePointerEvent) => {
            const dy = moveEvent.clientY - startY;

            if(!isDragging) {
                if(!getBottomSheetIsDragStarted({
                    slop: BOTTOM_SHEET_GESTURE.webSlop,
                    dx: 0,
                    dy
                })) {
                    return;
                }

                isDragging = true;

                sheetActions.current.onDragStart(isFromChrome);
            }

            const currentTime = Date.now();

            velocity = getBottomSheetVelocity({
                currentY: moveEvent.clientY,
                previousTime,
                currentTime,
                previousY,
                velocity
            });

            previousY = moveEvent.clientY;

            previousTime = currentTime;

            sheetActions.current.onDragMove(dy);
        };

        const onPointerUp = () => {
            removeDragListeners.current?.();

            if(isDragging) {
                sheetActions.current.onDragEnd(velocity, false);
            }
        };

        const onPointerCancel = () => {
            removeDragListeners.current?.();

            if(isDragging) {
                sheetActions.current.onDragEnd(0, true);
            }
        };

        safeGlobal.addEventListener("pointercancel", onPointerCancel);

        safeGlobal.addEventListener("pointermove", onPointerMove);

        safeGlobal.addEventListener("pointerup", onPointerUp);

        removeDragListeners.current = () => {
            safeGlobal.removeEventListener("pointercancel", onPointerCancel);

            safeGlobal.removeEventListener("pointermove", onPointerMove);

            safeGlobal.removeEventListener("pointerup", onPointerUp);

            removeDragListeners.current = undefined;
        };
    };

    const onBodyPointerDown = (event: PointerEvent) => {
        if(scrollViewProps?.onPointerDown) scrollViewProps.onPointerDown(event);

        isGestureInBody.current = true;
    };

    const onBodyTouchStart = (event: GestureResponderEvent) => {
        if(scrollViewProps?.onTouchStart) scrollViewProps.onTouchStart(event);

        isGestureInBody.current = true;
    };

    const onBodyLayout = (event: LayoutChangeEvent) => {
        if(scrollViewProps?.onLayout) scrollViewProps.onLayout(event);

        bodyHeight.current = event.nativeEvent.layout.height;

        syncRestingHeight();
    };

    const onBodyContentSizeChange = (width: number, height: number) => {
        if(scrollViewProps?.onContentSizeChange) scrollViewProps.onContentSizeChange(width, height);

        contentHeight.current = height;

        isScrollEndReached.current = false;

        syncRestingHeight();
    };

    const onBodyScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
        if(scrollViewProps?.onScroll) scrollViewProps.onScroll(event);

        if(gesture.current || momentumAnimation.current) {
            return;
        }

        scrollOffset.current = event.nativeEvent.contentOffset.y;

        updateIsScrolled(scrollOffset.current);

        checkScrollEnd();
    };

    const onOverlayPress = () => {
        if(onOverlayPressed) onOverlayPressed();

        if(isCloseOnOverlay) {
            requestClose();
        }
    };

    const getSheetGestureProps = () => {
        if(!isDragEnabled) {
            return {};
        }

        if(Platform.OS === "web") {
            return {
                onPointerDown: onSheetPointerDown
            };
        }

        return {
            ...panResponder.panHandlers,
            onTouchCancel: onSheetTouchEnd,
            onTouchEnd: onSheetTouchEnd
        };
    };

    const getBodyGestureProps = () => {
        if(!isDragEnabled) {
            return {};
        }

        if(Platform.OS === "web") {
            return {
                onPointerDown: onBodyPointerDown
            };
        }

        return {
            onTouchStart: onBodyTouchStart
        };
    };

    const renderHandle = () => {
        if(!isHandleVisible) {
            return null;
        }

        return <View
            style={[
                stylesheet.handleContainer,
                isDragEnabled ? stylesheet.draggableHandleContainer : null,
                handleContainerDynamicStyle
            ]}
            aria-hidden={true}
        >
            <View
                style={handleDynamicStyle}
            />
        </View>;
    };

    const renderTitle = () => {
        if(!isTitleRendered) {
            return null;
        }

        return <Text
            {...titleWebProps}
            customTheme={customTheme}
            variant="titleLargeSize"
            style={stylesheet.title}
            role="heading"
            color="high"
            id={titleId}
        >
            {title}
        </Text>;
    };

    const renderDescription = () => {
        if(!isDescriptionRendered) {
            return null;
        }

        return <Text
            customTheme={customTheme}
            variant="bodyMediumSize"
            id={descriptionId}
            color="mid"
        >
            {description}
        </Text>;
    };

    const renderHeaderContent = () => {
        if(headerContent) {
            return headerContent;
        }

        if(!isTitleRendered && !isDescriptionRendered) {
            return null;
        }

        return <View
            style={[
                stylesheet.headerTextContainer,
                headerTextContainerDynamicStyle
            ]}
        >
            {renderTitle()}
            {renderDescription()}
        </View>;
    };

    const renderClose = () => {
        if(!isCloseVisible) {
            return null;
        }

        return <Button
            accessibilityLabel={localize("close")}
            borderRadius={closeStyle.borderRadius}
            icon={BottomSheetCloseIcon}
            customTheme={customTheme}
            onPress={requestClose}
            spreadBehaviour="free"
            style={[
                stylesheet.close,
                closeDynamicStyle
            ]}
            variant="ghost"
            type="neutral"
            size="small"
        />;
    };

    const renderHeader = () => {
        if(!isHeaderVisible) {
            return null;
        }

        return <View
            style={[
                stylesheet.header,
                headerDynamicStyle
            ]}
        >
            {renderHeaderContent()}
            {renderClose()}
        </View>;
    };

    const renderLoading = () => {
        if(isContentReady) {
            return null;
        }

        return <View
            aria-label={localize("loading")}
            style={[
                stylesheet.loadingBody,
                loadingBodyDynamicStyle
            ]}
            role="progressbar"
            accessible={true}
        >
            <Loading
                customTheme={customTheme}
                size="large"
            />
        </View>;
    };

    const renderBody = () => {
        return <ScrollView
            {...scrollViewProps}
            {...getBodyGestureProps()}
            showsVerticalScrollIndicator={scrollViewProps?.showsVerticalScrollIndicator ?? false}
            keyboardShouldPersistTaps={scrollViewProps?.keyboardShouldPersistTaps ?? "handled"}
            style={[
                scrollViewStyle,
                stylesheet.body,
                isDragEnabled ? stylesheet.draggableBody : null
            ]}
            onContentSizeChange={onBodyContentSizeChange}
            contentContainerStyle={[
                scrollViewProps?.contentContainerStyle,
                bodyContentDynamicStyle
            ]}
            showsHorizontalScrollIndicator={false}
            scrollEnabled={isScrollEnabled}
            aria-busy={!isContentReady}
            onLayout={onBodyLayout}
            onScroll={onBodyScroll}
            scrollEventThrottle={1}
            overScrollMode="never"
            ref={scrollViewRef}
            bounces={false}
        >
            {renderLoading()}
            <View
                style={isContentReady ? null : stylesheet.hiddenContent}
            >
                <NCoreUIKitTheme.SurfaceProvider
                    surfaceColor={backgroundColor}
                >
                    {children}
                </NCoreUIKitTheme.SurfaceProvider>
            </View>
        </ScrollView>;
    };

    const renderFooter = () => {
        if(!isFooterVisible) {
            return null;
        }

        return <View
            style={[
                stylesheet.footer,
                footerDynamicStyle
            ]}
        >
            {footerContent}
        </View>;
    };

    const renderSheet = () => {
        return <Animated.View
            {...props}
            {...getSheetGestureProps()}
            style={[
                style,
                stylesheet.container,
                isDragEnabled ? stylesheet.draggableContainer : null,
                containerDynamicStyle,
                {
                    height: isHeightAnimated ? heightAnim : undefined,
                    transform: [
                        {
                            translateY: translateYAnim
                        }
                    ]
                }
            ]}
            onLayout={onSheetLayout}
        >
            {renderHandle()}
            {renderHeader()}
            {renderSubHeader?.()}
            {renderBody()}
            {renderFooter()}
        </Animated.View>;
    };

    return <Modal
        {...modalProps}
        aria-describedby={isDescriptionRendered ? descriptionId : modalProps?.["aria-describedby"]}
        aria-labelledby={isTitleRendered ? titleId : modalProps?.["aria-labelledby"]}
        portalName={portalName ? portalName : BOTTOM_SHEET_PORTAL_NAME}
        customLocalize={modalProps?.customLocalize ?? customLocalize}
        customTheme={modalProps?.customTheme ?? customTheme}
        isAnimated={modalProps?.isAnimated ?? true}
        key={`${bottomSheetKey.current}-modal`}
        onContainerLayout={onContainerLayout}
        role={modalProps?.role ?? "dialog"}
        isWorkWithPortal={isWorkWithPortal}
        onOverlayPress={onOverlayPress}
        isShowSurface={false}
        isShowClose={false}
        alignContent="free"
        isOpen={isActive}
        id={id.current}
        ref={modalRef}
    >
        {renderSheet()}
    </Modal>;
};
export default forwardRef(BottomSheet);
